<template>
  <div class="agent-source-matrix-report">
    <div class="bg-white">
      <h1 class="text-2xl font-bold text-gray-900 mb-6">
        Satışçı x Data Kaynağı Çapraz Matrisi
      </h1>

      <!-- Filtreler -->
      <div class="filters-container mb-6">
        <div class="flex flex-wrap gap-4 items-end">
          <div class="flex-1 min-w-[160px]">
            <label class="block text-sm font-medium text-gray-700 mb-2">Başlangıç Tarihi</label>
            <input
              v-model="startDate"
              type="date"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div class="flex-1 min-w-[160px]">
            <label class="block text-sm font-medium text-gray-700 mb-2">Bitiş Tarihi</label>
            <input
              v-model="endDate"
              type="date"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div class="flex-1 min-w-[200px]">
            <label class="block text-sm font-medium text-gray-700 mb-2">Gösterilecek Kaynak Sayısı</label>
            <select
              v-model.number="visibleSourceCount"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option :value="5">İlk 5 kaynak</option>
              <option :value="10">İlk 10 kaynak</option>
              <option :value="9999">Tüm kaynaklar</option>
            </select>
          </div>
          <div>
            <button
              @click="loadReport"
              :disabled="loading"
              class="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ loading ? 'Yükleniyor...' : 'Filtrele' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Hata -->
      <div v-if="errorMessage" class="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md">
        {{ errorMessage }}
      </div>

      <!-- Matris tablosu -->
      <div v-if="rows.length" class="overflow-x-auto border border-gray-200 rounded-lg">
        <table class="min-w-full bg-white text-sm">
          <thead>
            <tr class="bg-gray-100">
              <th
                @click="setSort('agent_name')"
                class="px-4 py-2 text-left font-semibold text-gray-700 border-b border-r sticky left-0 bg-gray-100 cursor-pointer select-none hover:bg-gray-200"
                rowspan="2"
              >
                Satış Temsilcisi<span class="text-blue-500">{{ sortArrow('agent_name') }}</span>
              </th>
              <th
                v-for="src in visibleSources"
                :key="src"
                colspan="3"
                class="px-4 py-2 text-center font-semibold text-gray-700 border-b border-r"
              >
                {{ src }}
              </th>
              <th
                @click="setSort('overall_cr')"
                class="px-4 py-2 text-center font-semibold text-gray-700 border-b cursor-pointer select-none hover:bg-gray-200"
                rowspan="2"
              >
                Genel CR %<span class="text-blue-500">{{ sortArrow('overall_cr') }}</span>
              </th>
            </tr>
            <tr class="bg-gray-50">
              <template v-for="src in visibleSources">
                <th :key="src + '-c'" @click="setSort('cell:' + src + ':customer')" class="px-3 py-2 text-right font-medium text-gray-500 border-b cursor-pointer select-none hover:bg-gray-200">Müşteri<span class="text-blue-500">{{ sortArrow('cell:' + src + ':customer') }}</span></th>
                <th :key="src + '-t'" @click="setSort('cell:' + src + ':clean_data')" class="px-3 py-2 text-right font-medium text-gray-500 border-b cursor-pointer select-none hover:bg-gray-200">Temiz Data<span class="text-blue-500">{{ sortArrow('cell:' + src + ':clean_data') }}</span></th>
                <th :key="src + '-r'" @click="setSort('cell:' + src + ':net_cr')" class="px-3 py-2 text-right font-medium text-gray-500 border-b border-r cursor-pointer select-none hover:bg-gray-200">Net CR %<span class="text-blue-500">{{ sortArrow('cell:' + src + ':net_cr') }}</span></th>
              </template>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr v-for="row in sortedRows" :key="row.agent_id" class="hover:bg-gray-50">
              <td class="px-4 py-3 text-gray-900 font-medium border-r sticky left-0 bg-white">{{ row.agent_name }}</td>
              <template v-for="src in visibleSources">
                <td :key="row.agent_id + src + '-c'" class="px-3 py-3 text-right text-gray-900">
                  {{ cell(row, src) ? formatInt(cell(row, src).customer) : '-' }}
                </td>
                <td :key="row.agent_id + src + '-t'" class="px-3 py-3 text-right text-gray-600">
                  {{ cell(row, src) ? formatInt(cell(row, src).clean_data) : '-' }}
                </td>
                <td :key="row.agent_id + src + '-r'" class="px-3 py-3 text-right font-semibold border-r"
                    :class="cell(row, src) ? crClass(cell(row, src).net_cr) : ''">
                  {{ cell(row, src) ? formatPct(cell(row, src).net_cr) : '-' }}
                </td>
              </template>
              <td class="px-4 py-3 text-right font-bold" :class="crClass(row.overall_cr)">
                {{ formatPct(row.overall_cr) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else-if="hasSearched && !loading" class="text-gray-500 py-8 text-center">
        Seçilen kriterlere uygun kayıt bulunamadı.
      </div>
    </div>
  </div>
</template>

<script>
import http from '../services/http'

export default {
  name: 'AgentSourceMatrixReport',
  data() {
    const today = new Date()
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
    return {
      startDate: firstDayOfMonth.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
      visibleSourceCount: 5,
      sources: [],
      rows: [],
      loading: false,
      hasSearched: false,
      errorMessage: '',
      sortField: null,
      sortDir: 'desc'
    }
  },
  computed: {
    visibleSources() {
      return this.sources.slice(0, this.visibleSourceCount)
    },
    sortedRows() {
      if (!this.sortField) return this.rows
      const dir = this.sortDir === 'asc' ? 1 : -1
      const field = this.sortField
      const valueOf = (row) => {
        if (field.startsWith('cell:')) {
          const [, src, prop] = field.split(':')
          const c = row.cells && row.cells[src]
          return c ? c[prop] : null
        }
        return row[field]
      }
      return [...this.rows].sort((a, b) => {
        const x = valueOf(a)
        const y = valueOf(b)
        // boş hücreleri (null) her zaman sona at
        if (x == null && y == null) return 0
        if (x == null) return 1
        if (y == null) return -1
        if (typeof x === 'string' || typeof y === 'string') {
          return String(x).localeCompare(String(y), 'tr') * dir
        }
        return (x - y) * dir
      })
    }
  },
  mounted() {
    this.loadReport()
  },
  methods: {
    async loadReport() {
      this.loading = true
      this.errorMessage = ''
      try {
        const response = await http.getAgentSourceMatrixReport({
          start_date: this.startDate,
          end_date: this.endDate
        })
        const res = response.data
        if (!res || res.success === false) {
          this.errorMessage = (res && res.message) || 'Rapor yüklenemedi'
          this.rows = []
          this.sources = []
        } else {
          this.sources = res.sources || []
          this.rows = res.rows || []
        }
      } catch (e) {
        this.errorMessage = 'Rapor yüklenirken bir hata oluştu'
      } finally {
        this.loading = false
        this.hasSearched = true
      }
    },
    cell(row, src) {
      return row.cells && row.cells[src] ? row.cells[src] : null
    },
    setSort(field) {
      if (this.sortField === field) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortField = field
        this.sortDir = field === 'agent_name' ? 'asc' : 'desc'
      }
    },
    sortArrow(field) {
      if (this.sortField !== field) return ''
      return this.sortDir === 'asc' ? ' ▲' : ' ▼'
    },
    formatInt(n) {
      return Number(n || 0).toLocaleString('tr-TR')
    },
    formatPct(n) {
      return '%' + Number(n || 0).toFixed(2)
    },
    crClass(v) {
      if (v >= 5) return 'text-green-700'
      if (v >= 2) return 'text-yellow-600'
      return 'text-red-600'
    }
  }
}
</script>

<style scoped>
.agent-source-matrix-report {
  @apply max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8;
}

.filters-container {
  @apply bg-gray-50 rounded-lg p-4;
}
</style>
